import moment from "moment";
import i18n from "i18next";
import { getTypeInfo } from "credit-card-type";
import states from "./usStates";
import { getI18nInstance } from "@p81-common/shell-core-js";
import { cycleNames, subscriptionType } from "@p81-common/shell-core-js";
import { hasAccess, resources, access } from "@p81-common/shell-core-js";
import { removeWordFromString } from "@p81-common/shell-core-js";
import { isCheckpointDomain } from "@p81-common/shell-core-js";
import { isSubscriptionActive } from "tools/isSubscriptionActive";

const { monthly, yearly, triennially, biennial } = cycleNames;
const regExp = new RegExp(
  `^([\\w-]+?)-(?:(msp)-)?(${monthly}|${yearly}|${triennially}|${biennial})-(\\w{1,3})$`
);

export function parsePlanId(planId) {
  if (planId) {
    let trial = false;
    if (planId.includes(subscriptionType.trial)) {
      trial = true;
      planId = removeWordFromString(planId, "-" + subscriptionType.trial);
    }

    let [, plan, type = "smb", cycle, currency] = planId.match(regExp) || [];
    return {
      plan,
      type,
      cycle,
      currency,
      trial,
    };
  }

  return {};
}

export function prettyCardNumber(cardNumber, cardType) {
  let card = getTypeInfo(cardType);

  if (card) {
    let offsets = [].concat(0, card.gaps, cardNumber.length);
    let components = [];

    for (let i = 0; offsets[i] < cardNumber.length; i++) {
      let start = offsets[i];
      let end = Math.min(offsets[i + 1], cardNumber.length);
      components.push(cardNumber.substring(start, end));
    }

    return components.join(" ");
  }

  return cardNumber;
}

let iconByType = {
  "american-express": require("assets/svg/billing-creditcard-american-express.svg"),
  "diners-club": require("assets/svg/billing-creditcard-diners.svg"),
  discover: require("assets/svg/billing-creditcard-discover.svg"),
  mastercard: require("assets/svg/billing-creditcard-mastercard.svg"),
  visa: require("assets/svg/billing-creditcard-visa.svg"),
  jcb: require("assets/svg/billing-creditcard-jcb.svg"),
  unionpay: require("assets/svg/billing-creditcard-unionpay.svg"),
  card: require("assets/svg/unknown-card.svg"),
  paypal: require("assets/svg/paypal-circle.svg"),
};

function getIconByType(type) {
  return iconByType[type] || iconByType.card;
}

let paymentTypesMap = {
  card: "card",
  paypal_express_checkout: "paypal",
};

function checkIsExpired(month, year) {
  let expDate = moment(`${year}-${month + 1}-01`, "Y-M-D");
  if (expDate.isBefore()) return "Expired";
  return `Expires on ${month}/${year}`;
}

export function parseMethod(m) {
  let type = paymentTypesMap[m.type];
  let isCard = type === "card";
  let brand = isCard ? m.card.brand || m.card.cardType : type;
  if (brand === "american_express" || brand === "diners_club") {
    brand = brand.replace("_", "-");
  }
  return {
    id: m.id,
    number: isCard
      ? prettyCardNumber(m.card.maskedNumber, brand)
      : m[type].email,
    brand,
    type,
    username: [m[type].first_name, m[type].last_name].filter(Boolean).join(" "),
    iconUrl: isCard ? getIconByType(brand) : iconByType[type],
    expDate: isCard
      ? checkIsExpired(m.card.expiryMonth, m.card.expiryYear)
      : "",
  };
}

let initialLicenses = {
  "addon-networkMember": {
    used: 0,
    total: 0,
    remaining: 0,
    handedOver: 0,
  },
  "addon-networkGateway": {
    used: 0,
    total: 0,
    remaining: 0,
    handedOver: 0,
  },
};

export function getLicenseInfo(data) {
  return data.reduce(
    (map, stat) => {
      map[stat.additionalType] = stat;
      return map;
    },
    { ...initialLicenses }
  );
}

export const addonNameByType = {
  member: "addon-networkMember",
  gateway: "addon-networkGateway",
  application: "addon-networkApplication",
  npr: "addon-networkPolicyRule",
  dpcRule: "addon-dpcRule",
  chinaBandwidth: "addon-china-bandwidth",
};

let MEMBER_LICENSES_REGEXP = /network-member|networkMember/;
let GATEWAY_LICENSES_REGEXP = /network-gateway|networkGateway/;
let SWG_MEMBER_REGEXP = /swg-member|swgMember/;

export function getAddonByEntityId(entityId) {
  if (MEMBER_LICENSES_REGEXP.test(entityId)) {
    return "addon-networkMember";
  }
  if (GATEWAY_LICENSES_REGEXP.test(entityId)) {
    return "addon-networkGateway";
  }
  if (SWG_MEMBER_REGEXP.test(entityId)) {
    return "addon-swgMember";
  }
  return "";
}

export function translateAddon(entityId) {
  return `billing:ADDONS.${getAddonByEntityId(entityId)}`;
}

export function translateHint(entityId) {
  return `billing:purchaseForm.hints.${getAddonByEntityId(entityId)}`;
}

export function translatePriceDetails(entityId) {
  return `billing:purchaseForm.priceDetails.${getAddonByEntityId(entityId)}`;
}

export const excludeExtraAddons = (items) => {
  const EXTRA_REGEXP = /extra/;

  return items.filter((item) => !EXTRA_REGEXP.test(item.entityId));
};

export function parseEstimateItems(items, { addonStatistic, mspType } = {}) {
  return Object.values(
    items
      .filter((i) => i.amount > 0)
      .reduce(
        (map, i) => {
          let key = getAddonByEntityId(i.entityId);

          if (key) {
            let markedForRemoval =
              addonStatistic?.find((a) => a.additionalType === key)
                ?.markedForRemoval || 0;
            if (map[key]) {
              map[key].amount += i.amount;
              map[key].discountAmount += i.discountAmount;
              map[key].quantity += i.quantity;
            } else {
              map[key] = { ...i, entityId: key, markedForRemoval };
            }
            if (mspType === "consolidated") {
              map[key].partnerDiscount = map[key].discountAmount;
            }
          }

          return map;
        },
        // preserve license type order
        {
          "addon-networkMember": null,
          "addon-networkGateway": null,
          "addon-swgMember": null,
        }
      )
  ).filter(Boolean);
}

export function prettyPrice(price = 0, currency = "USD") {
  return (price / 100).toLocaleString("en-US", { style: "currency", currency });
}

export function getAddonStatsByType(stats, type) {
  return stats.find((a) => a.additionalType === addonNameByType[type]) || {};
}

export function getPrimaryPaymentMethod(paymentSourcesData) {
  if (!paymentSourcesData) return null;
  let { primaryPaymentSourceId, paymentSources } = paymentSourcesData;
  return (
    paymentSources.find((s) => s.paymentSource.id === primaryPaymentSourceId) ||
    {}
  ).paymentSource;
}

let usRegionCountries = ["CA", "US"];

export function getStripeRegion(country) {
  return usRegionCountries.includes(country) ? "us" : "row";
}

let digitsReg = /^[0-9]+$/;

export function shouldBe5Digits(value) {
  if (value?.trim().length !== 5 || !digitsReg.test(value)) {
    return i18n.t("billing:paymentMethod.zip.error_message");
  }
}

export function shouldBe9Digits(value) {
  if (value?.trim().length !== 9 || !digitsReg.test(value)) {
    return i18n.t("billing:paymentMethod.reg_number.error_message");
  }
}

export let usStateOptions = states.map((state) => ({
  label: state,
  value: state,
}));

const usaZipReg = /^\d{5}(?:[-\s]\d{4})?$/;
export const validateUsaZip = (value) => {
  if (!usaZipReg.test(value)) {
    return getI18nInstance().t("billing:paymentMethod.zip.error_message");
  }
};

export { states };

export function getMSPDiscount(discounts, type, currency) {
  let discount = discounts?.find(
    (discount) =>
      discount.entityId?.startsWith(`discount-msp-consolidated-${type}`) &&
      discount.discountType === "item_level_coupon"
  )?.discountAmount;
  if (discount) {
    return prettyPrice(discount, currency);
  }
}

export function isOfflineTxAllowed(customer) {
  let { autoCollection, askForPaymentMethod = "yes" } = customer;
  return autoCollection === "off" && askForPaymentMethod === "no";
}

export function isPaymentMethodRequired(customer) {
  if (customer) {
    let { primaryPaymentSourceId } = customer;
    return !primaryPaymentSourceId && !isOfflineTxAllowed(customer);
  }
}

let askForPaymentMethod = null;

// see src/store/api/subscriptionGet/subscriptionGetSaga.js
export function setAskForPaymentMethod(value) {
  askForPaymentMethod = value;
}

let subscriptionStatus = null;

// see src/store/api/rootAccountGet/rootAccountGetSaga.js — kept in sync with every
// account fetch path (root account get, profile account get, child-org switch), same
// as setAddonPermissions/setFeatureFlags in that file.
export function setSubscriptionStatus(value) {
  subscriptionStatus = value;
}

export function isCpPlan(planId) {
  return Boolean(planId) && planId.toUpperCase().startsWith("CP-");
}

// MSP child tenants are excluded from Billing via the existing
// `hasPredefinedAccess(resources.BILLING, access.READ)` permission check at every call
// site — the MSP client role has no billing:read in the seeds.
//
// `status` defaults to the last value seen via setSubscriptionStatus (see
// rootAccountGetSaga.js) so callers that don't have it in scope (e.g. federated MFE call
// sites not yet updated to pass it explicitly) still get the live value instead of always
// reading "no subscription".
export function shouldShowBillingForPlan(planId, status = subscriptionStatus) {
  if (isCheckpointDomain && !isSubscriptionActive(status)) {
    return false; // Infinity Portal: billing only for tenants with an active subscription
  }
  if (!planId) {
    return true; // Default to showing billing if no plan ID
  }

  return !isCpPlan(planId);
}

export function isAllowBillingInfo() {
  return (
    askForPaymentMethod === "yes" ||
    !hasAccess(resources.BILLING_HIDE_INFO, access.READ)
  );
}
